import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const PMAYEligibilityPage = () => {
    const [settings, setSettings] = useState({
        section_title: 'Eligibility Criteria',
        section_subtitle: 'Find out if you are eligible for Aham PMAY Benefits'
    });
    const [tableData, setTableData] = useState({
        ews_income: 'Up to ₹3 lakh p.a.',
        lig_income: 'Up to ₹6 lakh p.a.',
        mig_income: 'Up to ₹9 lakh p.a.',
        house_ownership_text: 'Applicants must not own a pucca house in India. Property must be registered in the name of a female head or jointly.'
    });
    const [loanLimits, setLoanLimits] = useState({
        max_loan: '₹25 lakh',
        max_house_value: '₹35 lakh',
        max_carpet_area: '120 sqm'
    });
    const [subsidyDetails, setSubsidyDetails] = useState([
        'Interest Subsidy: 4% on first ₹8 lakh (up to 12 years).',
        'NPV: ₹1.50 lakh at 8.5% discount rate.',
        'Subsidy released in 5 yearly installments.',
        'Maximum subsidy: 1.80 lakh.'
    ]);
    const [additionalConditions, setAdditionalConditions] = useState([
        'Aadhaar mandatory for all family members.',
        'Self-declaration required.',
        'Must apply via official PMAY portal.',
        'Approved layout plans required.',
        'Geo-tagging mandatory for disbursement.',
        'Subsidy can be availed once only.'
    ]);
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [savingTable, setSavingTable] = useState(false);
    const [savingLimits, setSavingLimits] = useState(false);
    const [savingSubsidy, setSavingSubsidy] = useState(false);
    const [savingConditions, setSavingConditions] = useState(false);

    const LOAN_TYPE = 'pmay';

    // Fetch settings and table data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/admin/eligibility/settings/${LOAN_TYPE}`);
                if (response.data.data) {
                    setSettings(response.data.data);
                    // If table data exists in settings, use it
                    if (response.data.data.table_data) {
                        const parsed = JSON.parse(response.data.data.table_data);
                        setTableData(parsed);
                    }
                    // If loan limits data exists, use it
                    if (response.data.data.loan_limits) {
                        setLoanLimits(JSON.parse(response.data.data.loan_limits));
                    }
                    // If subsidy details exist, use them
                    if (response.data.data.subsidy_details) {
                        setSubsidyDetails(JSON.parse(response.data.data.subsidy_details));
                    }
                    // If additional conditions exist, use them
                    if (response.data.data.additional_conditions) {
                        setAdditionalConditions(JSON.parse(response.data.data.additional_conditions));
                    }
                }
            } catch (error) {
                console.error('Error fetching settings:', error);
                toast.error('Failed to load settings');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/eligibility/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // Handle table data update
    const handleTableSave = async () => {
        try {
            setSavingTable(true);
            const updatedSettings = {
                ...settings,
                table_data: JSON.stringify(tableData)
            };
            console.log('Saving table data:', updatedSettings);
            const response = await axios.put(`/api/admin/eligibility/settings/${LOAN_TYPE}`, updatedSettings);
            console.log('Save response:', response.data);
            setSettings(updatedSettings);
            toast.success('Table data updated successfully');
        } catch (error) {
            console.error('Error saving table data:', error);
            console.error('Error response:', error.response?.data);
            toast.error('Failed to update table data');
        } finally {
            setSavingTable(false);
        }
    };

    // Handle loan limits update
    const handleLimitsSave = async () => {
        try {
            setSavingLimits(true);
            const updatedSettings = {
                ...settings,
                loan_limits: JSON.stringify(loanLimits)
            };
            console.log('Saving loan limits:', updatedSettings);
            const response = await axios.put(`/api/admin/eligibility/settings/${LOAN_TYPE}`, updatedSettings);
            console.log('Save response:', response.data);
            setSettings(updatedSettings);
            toast.success('Loan limits updated successfully');
        } catch (error) {
            console.error('Error saving loan limits:', error);
            console.error('Error response:', error.response?.data);
            toast.error('Failed to update loan limits');
        } finally {
            setSavingLimits(false);
        }
    };

    // Handle subsidy details update
    const handleSubsidySave = async () => {
        try {
            setSavingSubsidy(true);
            const updatedSettings = {
                ...settings,
                subsidy_details: JSON.stringify(subsidyDetails)
            };
            const response = await axios.put(`/api/admin/eligibility/settings/${LOAN_TYPE}`, updatedSettings);
            setSettings(updatedSettings);
            toast.success('Subsidy details updated successfully');
        } catch (error) {
            console.error('Error saving subsidy details:', error);
            toast.error('Failed to update subsidy details');
        } finally {
            setSavingSubsidy(false);
        }
    };

    // Handle additional conditions update
    const handleConditionsSave = async () => {
        try {
            setSavingConditions(true);
            const updatedSettings = {
                ...settings,
                additional_conditions: JSON.stringify(additionalConditions)
            };
            const response = await axios.put(`/api/admin/eligibility/settings/${LOAN_TYPE}`, updatedSettings);
            setSettings(updatedSettings);
            toast.success('Additional conditions updated successfully');
        } catch (error) {
            console.error('Error saving additional conditions:', error);
            toast.error('Failed to update additional conditions');
        } finally {
            setSavingConditions(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 animate-pulse">
                    <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">PMAY - Eligibility Section</h1>
                    <p className="text-slate-500 mt-1">Manage the eligibility section for PMAY page</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Eligibility Criteria"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Find out if you're eligible..."
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Table Data Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Table Data Settings</h2>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                EWS Income
                            </label>
                            <input
                                type="text"
                                value={tableData.ews_income}
                                onChange={(e) => setTableData({ ...tableData, ews_income: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="e.g., Up to ₹3 lakh p.a."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                LIG Income
                            </label>
                            <input
                                type="text"
                                value={tableData.lig_income}
                                onChange={(e) => setTableData({ ...tableData, lig_income: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="e.g., Up to ₹6 lakh p.a."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                MIG Income
                            </label>
                            <input
                                type="text"
                                value={tableData.mig_income}
                                onChange={(e) => setTableData({ ...tableData, mig_income: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="e.g., Up to ₹9 lakh p.a."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            House Ownership Text
                        </label>
                        <textarea
                            value={tableData.house_ownership_text}
                            onChange={(e) => setTableData({ ...tableData, house_ownership_text: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            rows="3"
                            placeholder="e.g., Applicants must not own a pucca house..."
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleTableSave}
                        disabled={savingTable}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingTable ? 'Saving...' : 'Save Table Data'}
                    </button>
                </div>
            </div>

            {/* Loan & Property Limits Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Loan & Property Limits</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Max Loan
                        </label>
                        <input
                            type="text"
                            value={loanLimits.max_loan}
                            onChange={(e) => setLoanLimits({ ...loanLimits, max_loan: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., ₹25 lakh"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Max House Value
                        </label>
                        <input
                            type="text"
                            value={loanLimits.max_house_value}
                            onChange={(e) => setLoanLimits({ ...loanLimits, max_house_value: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., ₹35 lakh"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Max Carpet Area
                        </label>
                        <input
                            type="text"
                            value={loanLimits.max_carpet_area}
                            onChange={(e) => setLoanLimits({ ...loanLimits, max_carpet_area: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., 120 sqm"
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleLimitsSave}
                        disabled={savingLimits}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingLimits ? 'Saving...' : 'Save Loan Limits'}
                    </button>
                </div>
            </div>

            {/* Subsidy Details Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Subsidy Details</h2>

                <div className="space-y-3">
                    {subsidyDetails.map((detail, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                value={detail}
                                onChange={(e) => {
                                    const updated = [...subsidyDetails];
                                    updated[index] = e.target.value;
                                    setSubsidyDetails(updated);
                                }}
                                className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Subsidy detail ${index + 1}`}
                            />
                            <button
                                onClick={() => setSubsidyDetails(subsidyDetails.filter((_, i) => i !== index))}
                                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={() => setSubsidyDetails([...subsidyDetails, ''])}
                        className="w-full px-4 py-2 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 hover:border-primary-500 hover:text-primary-600"
                    >
                        + Add Subsidy Detail
                    </button>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSubsidySave}
                        disabled={savingSubsidy}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSubsidy ? 'Saving...' : 'Save Subsidy Details'}
                    </button>
                </div>
            </div>

            {/* Additional Conditions Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Additional Conditions</h2>

                <div className="space-y-3">
                    {additionalConditions.map((condition, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                value={condition}
                                onChange={(e) => {
                                    const updated = [...additionalConditions];
                                    updated[index] = e.target.value;
                                    setAdditionalConditions(updated);
                                }}
                                className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Condition ${index + 1}`}
                            />
                            <button
                                onClick={() => setAdditionalConditions(additionalConditions.filter((_, i) => i !== index))}
                                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={() => setAdditionalConditions([...additionalConditions, ''])}
                        className="w-full px-4 py-2 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 hover:border-primary-500 hover:text-primary-600"
                    >
                        + Add Condition
                    </button>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleConditionsSave}
                        disabled={savingConditions}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingConditions ? 'Saving...' : 'Save Additional Conditions'}
                    </button>
                </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">ℹ️ About This Section</h3>
                <p className="text-blue-800">
                    This page allows you to edit all PMAY eligibility and loan-related content including: section title/subtitle, income table, house ownership text, loan limits, subsidy details, and additional conditions.
                </p>
            </div>
        </div>
    );
};

export default PMAYEligibilityPage;
